/**
 * Klient pro vlastní backend (akční endpointy: scan, ZIP, presigned URL).
 */
const BACKEND_URL =
  import.meta.env.VITE_BACKEND_URL ?? "http://localhost:4000";

interface FetchOptions extends RequestInit {
  body?: BodyInit | null;
}

async function call<T>(path: string, init?: FetchOptions): Promise<T> {
  // Content-Type přidáváme jen pro requesty s body — Fastify 5 odmítá
  // empty body s deklarovaným JSON content-type.
  const hasBody = init?.body != null;
  const baseHeaders: Record<string, string> = hasBody
    ? { "Content-Type": "application/json" }
    : {};
  const res = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: {
      ...baseHeaders,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Backend error ${res.status}: ${text}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface CreateScanJobInput {
  from: string;
  to: string;
  accountIds: string[];
  foldersOverride?: Record<string, string[]>;
}

export interface ScanJobResponse {
  jobId: string;
  status: string;
}

export interface ScanJobStatus {
  id: string;
  status: string;
  totalEmails: number;
  processedEmails: number;
  extractedInvoices: number;
  errors: Array<{ accountId?: string; messageId?: string; error: string }>;
}

export interface ScanJobListItem {
  id: string;
  fromDate: string;
  toDate: string;
  status: string;
  totalEmails: number;
  processedEmails: number;
  extractedInvoices: number;
  errors: Array<{ accountId?: string; messageId?: string; error: string }>;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  requestedBy: string | null;
  emailAccountIds: string[];
}

export const api = {
  createScanJob: (input: CreateScanJobInput) =>
    call<ScanJobResponse>("/api/v1/scan-jobs", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  getScanJob: (id: string) => call<ScanJobStatus>(`/api/v1/scan-jobs/${id}`),

  listScanJobs: (limit = 50) =>
    call<{ jobs: ScanJobListItem[] }>(
      `/api/v1/scan-jobs?limit=${limit}`,
    ),

  getDownloadUrl: (invoiceId: string) =>
    call<{ url: string }>(`/api/v1/invoices/${invoiceId}/download-url`),

  deleteInvoice: (invoiceId: string) =>
    call<void>(`/api/v1/invoices/${invoiceId}`, { method: "DELETE" }),

  changeInvoiceCompany: (invoiceId: string, companyId: string) =>
    call<void>(`/api/v1/invoices/move`, {
      method: "POST",
      body: JSON.stringify({ id: invoiceId, companyId }),
    }),

  zipUrl: () => `${BACKEND_URL}/api/v1/invoices/zip`,
};
